import type { FC, ReactNode } from 'react';
import type { ArticleHead } from './types';
import { formatRelativeDate } from '../../utils/date-format';
import { Badge } from '../../components/ui/badge';
import { useTranslation } from '../../i18n/I18nContext';

interface ArticleMetadataProps {
  article: ArticleHead;
  rightContent?: ReactNode;
}

const ArticleMetadata: FC<ArticleMetadataProps> = ({
  article,
  rightContent,
}) => {
  const { t } = useTranslation();
  const imageUrl = article.img
    ? `${import.meta.env.VITE_API_URL}/reader/articles/images/${article.img}`
    : null;

  return (
    <div className="mb-4">
      {imageUrl ? (
        <div className="mb-3 rounded-lg overflow-hidden bg-muted">
          <img
            src={imageUrl}
            alt={article.title}
            className="w-full h-36 object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      ) : (
        <div className="mb-3 rounded-lg bg-muted h-36" />
      )}

      <div className="flex items-center justify-between gap-1.5 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          {article.lang && (
            <Badge variant="indigo" title={t('badge.language')}>
              {article.lang.toUpperCase()}
            </Badge>
          )}
          {article.cefrLevel && (
            <Badge variant="pink" title={t('badge.cefr')}>
              {article.cefrLevel}
            </Badge>
          )}
          {article.wordCount != null && (
            <Badge variant="secondary" title={t('badge.word_count')}>
              {t('article_card.words', { count: article.wordCount })}
            </Badge>
          )}
          {article.title && article.description && article.img && (article.timestamp || article.createdAt) && (
            <Badge variant="secondary" title={t('badge.date')}>
              {formatRelativeDate(article.timestamp || article.createdAt!, t)}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {rightContent}
        </div>
      </div>
    </div>
  );
};

export default ArticleMetadata;
